import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../../context/AdminAuthContext';

 const ChangePasswordModal = ({
  show,
  onClose,
  passwordFormData,
  onPasswordFormChange,
  onSubmit,
  isLoading
}) => {
  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">تغيير كلمة المرور</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور الحالية</label>
              <input
                type="password"
                required
                value={passwordFormData.oldPassword || ''}
                onChange={(e) => onPasswordFormChange('oldPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="أدخل كلمة المرور الحالية..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور الجديدة</label>
              <input
                type="password"
                required
                value={passwordFormData.newPassword || ''}
                onChange={(e) => onPasswordFormChange('newPassword', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="أدخل كلمة المرور الجديدة..."
              />
            </div>
          </div>
          
          <div className="flex gap-2 justify-end border-t pt-4 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  جاري التغيير...
                </>
              ) : 'تغيير كلمة المرور'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

 const Notification = ({ notification }) => {
  if (!notification) return null;
  
  const bgColor = notification.type === 'success' ? 'bg-green-500' : 'bg-red-500';
  
  return (
    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg text-white shadow-lg z-50 ${bgColor} animate-fade-in-out`}>
      <div className="flex items-center">
        {notification.type === 'success' ? (
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        ) : (
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        )}
        <span>{notification.message}</span>
      </div>
    </div>
  );
};

 const SkeletonCard = () => (
  <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
    <div className="flex space-x-2">
      <div className="h-8 bg-gray-200 rounded w-20"></div>
      <div className="h-8 bg-gray-200 rounded w-20"></div>
    </div>
  </div>
);

 const EditStatisticsModal = ({ 
  show, 
  onClose, 
  statisticsFormData, 
  onStatisticsFormChange, 
  onStatisticsLabelChange, 
  onSubmit, 
  isLoading 
}) => {
  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">تعديل الإحصائيات</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">الطلاب النشطين</label>
              <input
                type="text"
                required
                value={statisticsFormData.activeStudents || ''}
                onChange={(e) => onStatisticsFormChange('activeStudents', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="مثال: 1600+"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">المراجعات الأسبوعية</label>
              <input
                type="text"
                required
                value={statisticsFormData.weeklyReviews || ''}
                onChange={(e) => onStatisticsFormChange('weeklyReviews', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="مثال: 500+"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">نسبة الرضا</label>
              <input
                type="text"
                required
                value={statisticsFormData.satisfactionRate || ''}
                onChange={(e) => onStatisticsFormChange('satisfactionRate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="مثال: 95%"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">الدعم المتواصل</label>
              <input
                type="text"
                required
                value={statisticsFormData.supportAvailability || ''}
                onChange={(e) => onStatisticsFormChange('supportAvailability', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="مثال: 24/7"
              />
            </div>
            
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-3">تسميات الإحصائيات</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تسمية الطلاب النشطين</label>
                  <input
                    type="text"
                    required
                    value={statisticsFormData.labels?.activeStudentsLabel || ''}
                    onChange={(e) => onStatisticsLabelChange('activeStudentsLabel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="مثال: طالب نشط"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تسمية المراجعات الأسبوعية</label>
                  <input
                    type="text"
                    required
                    value={statisticsFormData.labels?.weeklyReviewsLabel || ''}
                    onChange={(e) => onStatisticsLabelChange('weeklyReviewsLabel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="مثال: مراجعة أسبوعية"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تسمية نسبة الرضا</label>
                  <input
                    type="text"
                    required
                    value={statisticsFormData.labels?.satisfactionRateLabel || ''}
                    onChange={(e) => onStatisticsLabelChange('satisfactionRateLabel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="مثال: نسبة الرضا"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تسمية الدعم المتواصل</label>
                  <input
                    type="text"
                    required
                    value={statisticsFormData.labels?.supportAvailabilityLabel || ''}
                    onChange={(e) => onStatisticsLabelChange('supportAvailabilityLabel', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="مثال: دعم متواصل"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end border-t pt-4 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  جاري التحديث...
                </>
              ) : 'تحديث الإحصائيات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

 const AddAdminModal = ({ 
  show, 
  onClose, 
  adminFormData, 
  onAdminFormChange, 
  onSubmit, 
  isLoading, 
  canManageAdmins 
}) => {
  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">إضافة مدير جديد</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">اسم المدير</label>
              <input
                type="text"
                required
                value={adminFormData.name}
                onChange={(e) => onAdminFormChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="أدخل اسم المدير..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">البريد الإلكتروني</label>
              <input
                type="email"
                required
                value={adminFormData.email}
                onChange={(e) => onAdminFormChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="أدخل البريد الإلكتروني..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور</label>
              <input
                type="password"
                required
                value={adminFormData.password}
                onChange={(e) => onAdminFormChange('password', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="أدخل كلمة المرور..."
              />
            </div>
          </div>
          
          <div className="flex gap-2 justify-end border-t pt-4 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={isLoading || !canManageAdmins}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  جاري الإضافة...
                </>
              ) : 'إضافة المدير'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

 const DeleteConfirmation = ({ 
  show, 
  admin, 
  onCancel, 
  onConfirm, 
  canManageAdmins 
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="text-center mb-6">
          <div className="bg-red-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">تأكيد حذف المدير</h2>
          <p className="text-gray-600">
            هل أنت متأكد أنك تريد حذف المدير "{admin?.name}"؟ 
            <br />
            هذا الإجراء لا يمكن التراجع عنه.
          </p>
        </div>
        
        <div className="flex gap-2 justify-center">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            إلغاء
          </button>
          <button
            onClick={onConfirm}
            disabled={!canManageAdmins}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            نعم، احذف المدير
          </button>
        </div>
      </div>
    </div>
  );
};

const StatisticsAndAdminsManagement = () => {
  const { adminToken, admin } = useAdminAuth();
  const queryClient = useQueryClient();
  
   const [showEditStatisticsModal, setShowEditStatisticsModal] = useState(false);
  const [statisticsFormData, setStatisticsFormData] = useState({});
  
   const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [adminFormData, setAdminFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  
   const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    oldPassword: '',
    newPassword: ''
  });
  
   const [notification, setNotification] = useState(null);

   const fetchStatistics = async () => {
    try {
      const response = await fetch('https://question-manager-backend.onrender.com/api/statis', {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('فشل في جلب الإحصائيات');
      }
      
      return response.json();
    } catch (error) {
      showNotification('error', 'فشل في جلب الإحصائيات: ' + error.message);
      throw error;
    }
  };

   const fetchAdmins = async () => {
    try {
      const response = await fetch('https://question-manager-backend.onrender.com/api/auth/admins', {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('فشل في جلب المدراء');
      }
      
      return response.json();
    } catch (error) {
      showNotification('error', 'فشل في جلب المدراء: ' + error.message);
      throw error;
    }
  };

   const { data: statisticsData, isLoading: statisticsLoading, error: statisticsError } = useQuery({
    queryKey: ['statistics'],
    queryFn: fetchStatistics,
  });

  const { data: adminsData, isLoading: adminsLoading, error: adminsError } = useQuery({
    queryKey: ['admins'],
    queryFn: fetchAdmins,
  });

   const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

   const changePasswordMutation = useMutation({
    mutationFn: async (passwordData) => {
      try {
        const response = await fetch('https://question-manager-backend.onrender.com/api/auth/admin/change-password', {
          method: 'put',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(passwordData),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'فشل في تغيير كلمة المرور');
        }
        
        return data;
      } catch (error) {
        showNotification('error', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم تغيير كلمة المرور بنجاح!');
      setShowChangePasswordModal(false);
      setPasswordFormData({
        oldPassword: '',
        newPassword: ''
      });
    },
    onError: (error) => {
      showNotification('error', error.message);
    }
  });

   const updateStatisticsMutation = useMutation({
    mutationFn: async (updatedStatistics) => {
      try {
        const response = await fetch(`https://question-manager-backend.onrender.com/api/statis/${statisticsData.statistics.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedStatistics),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في تحديث الإحصائيات');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في تحديث الإحصائيات: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم تحديث الإحصائيات بنجاح!');
      queryClient.invalidateQueries(['statistics']);
      setShowEditStatisticsModal(false);
    },
  });

   const addAdminMutation = useMutation({
    mutationFn: async (newAdmin) => {
      try {
         if (admin?.role !== 'superadmin') {
          throw new Error('ليس لديك صلاحيات لإضافة مدير جديد');
        }
        
        const response = await fetch('https://question-manager-backend.onrender.com/api/auth/create-admin', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newAdmin),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في إضافة المدير');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في إضافة المدير: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم إضافة المدير بنجاح!');
      queryClient.invalidateQueries(['admins']);
      setShowAddAdminModal(false);
      setAdminFormData({
        name: '',
        email: '',
        password: ''
      });
    },
  });

   const deleteAdminMutation = useMutation({
    mutationFn: async (adminId) => {
      try {
         if (admin?.role !== 'superadmin') {
          throw new Error('ليس لديك صلاحيات لحذف مدير');
        }
        
        const response = await fetch(`https://question-manager-backend.onrender.com/api/auth/admin/${adminId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في حذف المدير');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في حذف المدير: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم حذف المدير بنجاح');
      queryClient.invalidateQueries(['admins']);
      setShowDeleteConfirmation(false);
      setAdminToDelete(null);
    },
  });

   const handleEditStatistics = () => {
    if (statisticsData && statisticsData.statistics) {
      setStatisticsFormData({
        activeStudents: statisticsData.statistics.activeStudents,
        weeklyReviews: statisticsData.statistics.weeklyReviews,
        satisfactionRate: statisticsData.statistics.satisfactionRate,
        supportAvailability: statisticsData.statistics.supportAvailability,
        labels: { ...statisticsData.statistics.labels }
      });
      setShowEditStatisticsModal(true);
    }
  };

   const handleSubmitStatistics = () => {
    updateStatisticsMutation.mutate(statisticsFormData);
  };

   const handleSubmitAdmin = () => {
    addAdminMutation.mutate(adminFormData);
  };

   const handleChangePassword = () => {
    changePasswordMutation.mutate(passwordFormData);
  };

   const handleDeleteAdmin = () => {
    if (adminToDelete) {
      deleteAdminMutation.mutate(adminToDelete.id);
    }
  };

   const handleStatisticsFormChange = (field, value) => {
    setStatisticsFormData(prev => ({ 
      ...prev, 
      [field]: value 
    }));
  };

  const handleStatisticsLabelChange = (field, value) => {
    setStatisticsFormData(prev => ({ 
      ...prev, 
      labels: {
        ...prev.labels,
        [field]: value
      }
    }));
  };

  const handleAdminFormChange = (field, value) => {
    setAdminFormData(prev => ({ 
      ...prev, 
      [field]: value 
    }));
  };

  const handlePasswordFormChange = (field, value) => {
    setPasswordFormData(prev => ({ 
      ...prev, 
      [field]: value 
    }));
  };

   const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

   if (statisticsLoading || adminsLoading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <SkeletonCard key={item} />
            ))}
          </div>
        </div>
      </div>
    );
  }

   if (statisticsError || adminsError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-red-100">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">حدث خطأ</h2>
          <p className="text-gray-600 mb-6">{statisticsError?.message || adminsError?.message}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

   const { statistics } = statisticsData || { statistics: {} };
  const { admins, count } = adminsData || { admins: [], count: 0 };

   const canManageAdmins = admin?.role === 'superadmin';

  return (
    <div className="min-h-screen bg-gray-100">
       <Notification notification={notification} />
      
       <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto p-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">الإحصائيات وإدارة المدراء</h1>
              {/* <p className="text-gray-600 text-sm">
                يمكنك إدارة إحصائيات المنصة والمدراء من هنا
              </p> */}
            </div>
            <div className="flex gap-2 self-start">
              <button
                onClick={() => setShowChangePasswordModal(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
                تغيير كلمة المرور
              </button>
              <button
                onClick={handleEditStatistics}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
                تعديل الإحصائيات
              </button>
              {canManageAdmins && (
                <button
                  onClick={() => setShowAddAdminModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  إضافة مدير
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

       <div className="bg-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-4 text-white shadow-lg">
              <p className="text-sm mb-1">{statistics.labels?.activeStudentsLabel || 'الطلاب النشطين'}</p>
              <div className="flex items-end">
                <span className="text-2xl md:text-3xl font-bold">{statistics.activeStudents || '0'}</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg p-4 text-white shadow-lg">
              <p className="text-sm mb-1">{statistics.labels?.weeklyReviewsLabel || 'المراجعات الأسبوعية'}</p>
              <div className="flex items-end">
                <span className="text-2xl md:text-3xl font-bold">{statistics.weeklyReviews || '0'}</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg p-4 text-white shadow-lg">
              <p className="text-sm mb-1">{statistics.labels?.satisfactionRateLabel || 'نسبة الرضا'}</p>
              <div className="flex items-end">
                <span className="text-2xl md:text-3xl font-bold">{statistics.satisfactionRate || '0%'}</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg p-4 text-white shadow-lg">
              <p className="text-sm mb-1">{statistics.labels?.supportAvailabilityLabel || 'الدعم المتواصل'}</p>
              <div className="flex items-end">
                <span className="text-2xl md:text-3xl font-bold">{statistics.supportAvailability || '0'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

       <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">إدارة المدراء</h2>
            <div className="text-gray-600">
              <span className="font-medium">{count}</span> مدير
            </div>
          </div>
        </div>

        {admins.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-indigo-100">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">لا توجد مدراء حالياً</h3>
            <p className="text-gray-600 mb-6">قم بإضافة مدير جديد للبدء</p>
            {canManageAdmins && (
              <button
                onClick={() => setShowAddAdminModal(true)}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                إضافة مدير جديد
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {admins.map((admin) => (
              <div key={admin.id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 transition-all hover:shadow-md">
                <div className={`h-2 ${admin.role === 'superadmin' ? 'bg-purple-500' : 'bg-indigo-500'}`}></div>
                <div className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-gray-800">{admin.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      admin.role === 'superadmin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-indigo-100 text-indigo-800'
                    }`}>
                      {admin.role === 'superadmin' ? 'مدير عام' : 'مدير'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4">
                    {admin.email}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-4">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span>تاريخ الإنشاء</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span>{formatDate(admin.createdAt)}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    {canManageAdmins && (
                      <button
                        onClick={() => {
                          setAdminToDelete(admin);
                          setShowDeleteConfirmation(true);
                        }}
                        disabled={admin.role === 'superadmin'}
                        className={`p-2 text-red-600 rounded-md transition-colors flex-1 flex items-center justify-center ${
                          admin.role === 'superadmin' 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-red-50 hover:bg-red-100'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                        <span className="mr-1">حذف</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

       <ChangePasswordModal
        show={showChangePasswordModal}
        onClose={() => {
          setShowChangePasswordModal(false);
          setPasswordFormData({
            oldPassword: '',
            newPassword: ''
          });
        }}
        passwordFormData={passwordFormData}
        onPasswordFormChange={handlePasswordFormChange}
        onSubmit={handleChangePassword}
        isLoading={changePasswordMutation.isLoading}
      />

      <EditStatisticsModal
        show={showEditStatisticsModal}
        onClose={() => setShowEditStatisticsModal(false)}
        statisticsFormData={statisticsFormData}
        onStatisticsFormChange={handleStatisticsFormChange}
        onStatisticsLabelChange={handleStatisticsLabelChange}
        onSubmit={handleSubmitStatistics}
        isLoading={updateStatisticsMutation.isLoading}
      />

      <AddAdminModal
        show={showAddAdminModal}
        onClose={() => setShowAddAdminModal(false)}
        adminFormData={adminFormData}
        onAdminFormChange={handleAdminFormChange}
        onSubmit={handleSubmitAdmin}
        isLoading={addAdminMutation.isLoading}
        canManageAdmins={canManageAdmins}
      />
      
      <DeleteConfirmation
        show={showDeleteConfirmation}
        admin={adminToDelete}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setAdminToDelete(null);
        }}
        onConfirm={handleDeleteAdmin}
        canManageAdmins={canManageAdmins}
      />
      
      <style jsx="true">{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -20px); }
          10% { opacity: 1; transform: translate(-50%, 0); }
          90% { opacity: 1; transform: translate(-50%, 0); }
          100% { opacity: 0; transform: translate(-50%, -20px); }
        }
        
        .animate-fade-in-out {
          animation: fadeInOut 3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default StatisticsAndAdminsManagement;